/**
 * Payment state — SINGLE SOURCE OF TRUTH (frontend mirror of
 * backend services/financial_utils.py::get_payment_state).
 *
 * Determines whether a booking is paid / deposit-held / unpaid with a
 * forgiving threshold so already-bought (legacy) tickets are NEVER falsely
 * flagged unpaid due to rounding, fees, or partial-cent drift.
 */

// Within 50 cents of the total counts as FULLY PAID.
export const PAID_FORGIVENESS = 0.5;

// payment_status values that always read as settled (paid) regardless of amounts.
const SETTLED_STATUSES = new Set([
  'paid', 'completed', 'refunded', 'partial_refund', 'partially_refunded',
]);
// payment_status values that mean a deposit/partial was collected (retained for
// reference/documentation — a real deposit is decided by actual amount paid).
// eslint-disable-next-line no-unused-vars
const DEPOSIT_STATUSES = new Set(['deposit_paid', 'partial', 'partially_paid']);

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Total the customer owes (org base currency). Handles both camelCase + snake_case shapes. */
export function getBookingTotal(b) {
  return round2(
    num(b?.totalAmount ?? b?.total_amount) ||
    num(b?.totalPrice ?? b?.total_price) ||
    num(b?.servicePrice ?? b?.service_price)
  );
}

/** How much has actually been collected. */
export function getBookingPaid(b) {
  return round2(Math.max(
    num(b?.paidAmount ?? b?.paid_amount),
    num(b?.amountPaid ?? b?.amount_paid),
    num(b?.paidAmountBase ?? b?.paid_amount_base),
    num(b?.depositPaid ?? b?.deposit_paid)
  ));
}

/**
 * @returns {{state:'paid'|'deposit'|'unpaid', total:number, paid:number, remaining:number}}
 */
export function getPaymentState(b) {
  if (!b) return { state: 'unpaid', total: 0, paid: 0, remaining: 0 };
  const status = String(b.paymentStatus ?? b.payment_status ?? '').toLowerCase();
  const total = getBookingTotal(b);
  const paid = getBookingPaid(b);
  const remaining = round2(total - paid);

  if (SETTLED_STATUSES.has(status)) {
    return { state: 'paid', total, paid, remaining: Math.max(0, remaining) };
  }
  if (total <= 0 || remaining <= PAID_FORGIVENESS) {
    return { state: 'paid', total, paid, remaining: 0 };
  }
  // A real deposit requires actual money on record — a deposit/partial status
  // label with $0 collected is surfaced as 'unpaid' (pay-gate shown) so we
  // never render a contradictory "Deposit paid $0.00" card.
  if (paid > PAID_FORGIVENESS) {
    return { state: 'deposit', total, paid, remaining };
  }
  return { state: 'unpaid', total, paid, remaining };
}

export const isUnpaid = (b) => getPaymentState(b).state === 'unpaid';
export const isPaidOrDeposit = (b) => getPaymentState(b).state !== 'unpaid';

/**
 * Canonical booking-status badge — SINGLE SOURCE OF TRUTH so the customer
 * ticket shows the EXACT same status the operator sees in EditBookingModal /
 * Schedule. Booking-level states (cancelled/completed) take priority, otherwise
 * we mirror the operator's raw payment_status pill labels & colors verbatim.
 *
 * @returns {{key:string, label:string, classes:string, dot:string}}
 */
export function getBookingStatusBadge(b) {
  const bStatus = String(b?.status ?? b?.bookingStatus ?? b?.booking_status ?? '').toLowerCase();
  const pStatus = String(b?.paymentStatus ?? b?.payment_status ?? '').toLowerCase();

  if (bStatus === 'cancelled' || bStatus === 'canceled') {
    return { key: 'cancelled', label: 'Cancelled', classes: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' };
  }
  if (bStatus === 'completed') {
    return { key: 'completed', label: 'Completed', classes: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' };
  }
  // Mirror EditBookingModal's payment-status pill exactly:
  switch (pStatus) {
    case 'paid':
      return { key: 'paid', label: 'Paid', classes: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' };
    case 'deposit_paid':
      return { key: 'deposit_paid', label: 'Deposit', classes: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' };
    case 'partial':
    case 'partially_paid':
      return { key: 'partial', label: 'Partial', classes: 'bg-yellow-50 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' };
    case 'refunded':
      return { key: 'refunded', label: 'Refunded', classes: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400' };
    case 'partially_refunded':
    case 'partial_refund':
      return { key: 'partially_refunded', label: 'Part Refund', classes: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' };
    default:
      return { key: 'unpaid', label: 'Unpaid', classes: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' };
  }
}
